/**
 * Provider errors (SECURITY §3): every failure is rebuilt as `{status, type, message}`. Nothing from a request, a header or
 * the caught exception is copied through, so a key can never reach a log, a report or an export.
 */
export interface ProviderErrorInfo {
	status: number;
	type: string;
	message: string;
	/**
	 * The server's `retry-after` in milliseconds on a 429/529/5xx, when it sent a readable one (BENCH-V3-PLAN §3.3: the limiter
	 * holds new launches until it has passed). A number, never header text, so nothing from a response is copied through.
	 * Not part of `toJSON()`.
	 */
	retryAfterMs?: number | null;
}

export class ProviderError extends Error implements ProviderErrorInfo {
	readonly status: number;
	readonly type: string;
	readonly retryAfterMs: number | null;
	constructor(info: ProviderErrorInfo) {
		super(info.message);
		this.name = 'ProviderError';
		this.status = info.status;
		this.type = info.type;
		this.retryAfterMs = info.retryAfterMs ?? null;
	}
	toJSON(): ProviderErrorInfo {
		return { status: this.status, type: this.type, message: this.message };
	}
}

/** Thrown when the 1.5× budget guard has paused the job. */
export class BudgetPausedError extends ProviderError {
	constructor(message: string) {
		super({ status: 0, type: 'budget_paused', message });
		this.name = 'BudgetPausedError';
	}
}

/** Removes any occurrence of secret strings (the key, the dev token) and control characters, and bounds the length. */
export function scrub(text: string, secrets: readonly string[]): string {
	let out = text;
	for (const s of secrets) if (s.length >= 4) out = out.split(s).join('[redacted]');
	// biome-ignore lint/suspicious/noControlCharactersInRegex: strip control characters from upstream text
	return out.replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, 300);
}

/** Builds the error for an API error body (`{type:'error', error:{type, message}}`) or a plain failure. */
export function errorFrom(
	status: number,
	body: unknown,
	secrets: readonly string[]
): ProviderError {
	const inner = (body as { error?: { type?: unknown; message?: unknown } } | null)?.error;
	const type =
		typeof inner?.type === 'string' ? inner.type : status === 0 ? 'network_error' : 'api_error';
	const message = typeof inner?.message === 'string' ? inner.message : `request failed (${status})`;
	return new ProviderError({
		status,
		type: scrub(type, secrets).slice(0, 60),
		message: scrub(message, secrets),
	});
}
